import axiosConfig from "./axiosConfig";

export interface ApiCostTotals {
  runCount: number;
  testRunCount: number;
  failedRunCount: number;
  modelUsd: number;
  webSearchUsd: number;
  totalUsd: number;
  webSearchCount: number;
  // Apollo enrichment credits (a non-USD budget), so a contacts-enrichment run
  // shows a real credit count rather than a $0 row.
  apolloCredits: number;
  // Apollo discovery calls (free but call-capped), so a people-discovery run shows a
  // real call count rather than looking like a $0 no-op.
  apolloSearchCalls: number;
}

export interface ApiCostRun {
  id: string;
  createdAt: string;
  // The ledger `source` value - the page routes the AI-trace link by this.
  source: string | null;
  // Operator-facing name for that source, resolved by the BE from its registry.
  // Falls back to the raw source for a surface not yet registered there.
  sourceLabel: string;
  // Coarse grouping ("Leads", "Operational", ...) for the filter chips.
  sourceGroup: string;
  // The trace subject ref, and the first half of the trace lookup key.
  subjectRef: string | null;
  // What the spend was FOR, resolved per source: a lead's address, a site's name.
  // Null when unresolvable (deleted, or absent from this env) - show the ref.
  subjectLabel: string | null;
  // Which side a Contacts-ranking / enrichment run looked at; null otherwise.
  subjectRole: "landlord" | "tenant" | null;
  isTest: boolean;
  failed: boolean;
  durationSeconds: number | null;
  error: string | null;
  webSearchCount: number;
  apolloCredits: number | null;
  apolloSearchCalls: number | null;
  modelUsd: number;
  webSearchUsd: number;
  totalUsd: number;
  traceSubjectRef: string | null;
  traceRunId: string | null;
}

export interface ApiCostSourceTotals {
  runCount: number;
  modelUsd: number;
  webSearchUsd: number;
  totalUsd: number;
}

export interface ApiCostSource {
  source: string;
  label: string;
  group: string;
}

export interface ApiCostsResponse {
  windowDays: number;
  generatedAt: string;
  // Costs are ESTIMATES computed from per-model token + web-search counts on read.
  webSearchUsdPerQuery: number;
  // Every spending surface the BE knows how to label, for the filter control.
  sources: ApiCostSource[];
  totals: ApiCostTotals;
  // Spend split by source label. Keyed on the raw ledger `source`, and it can
  // contain sources absent from `sources` above - an unregistered surface still
  // has its spend counted rather than hidden.
  bySource: Record<string, ApiCostSourceTotals>;
  // Spend per calendar day, oldest first, for the trend strip.
  byDay: { day: string; totalUsd: number }[];
  recent: ApiCostRun[];
  truncated: boolean;
}

const getApiCosts = async (
  authToken: string,
  days: number,
  source?: string | null
): Promise<ApiCostsResponse> => {
  const { data } = await axiosConfig.get(`/admin/api-costs`, {
    params: source ? { days, source } : { days },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export default getApiCosts;
